import { datadogRum } from '@datadog/browser-rum'

import type { FlexPortion } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

type FlexPortionTrackingSource =
  | 'menu_search_screen'
  | 'modal_basket_screen'
  | 'view_recipe_detail_screen'
  | 'view_recipe_list_screen'

export type FlexPortionTrackingContext = {
  menuUuid?: string | null
  recipeUuid: RecipeUUID
  recipePortionCount: number
  basketRecipeCount: number
  source: FlexPortionTrackingSource
}

type FlexPortionTrackingProperties = {
  basket_recipe_count: number
  menu_uuid?: string | null
  recipe_portion_count: number
  recipe_uuid: RecipeUUID
  source: FlexPortionTrackingSource
}

type ClickEditRecipeQuantityEvent = {
  action: 'click_edit_recipe_quantity'
  properties: FlexPortionTrackingProperties
}

type SelectPortionCountEvent = {
  action: 'select_portion_count'
  properties: FlexPortionTrackingProperties
}

type UpdatePortionEvent = {
  action: 'update_portion'
  properties: FlexPortionTrackingProperties
}

type EditRecipeQuantityLoadedEvent = {
  action: 'edit_recipe_quantity_loaded'
  properties: {
    available_portion_options: string[]
    menu_uuid?: string | null
    recipe_uuid: RecipeUUID
    unavailable_portion_options: string[]
  }
}

const getCommonProperties = (
  context: FlexPortionTrackingContext,
  recipePortionCount = context.recipePortionCount,
): FlexPortionTrackingProperties => ({
  basket_recipe_count: context.basketRecipeCount,
  menu_uuid: context.menuUuid,
  recipe_portion_count: recipePortionCount,
  recipe_uuid: context.recipeUuid,
  source: context.source,
})

export const trackClickEditRecipeQuantity = (context: FlexPortionTrackingContext) => {
  queueTrackingEvent<ClickEditRecipeQuantityEvent>({
    action: 'click_edit_recipe_quantity',
    properties: getCommonProperties(context),
  })
}

type PortionAvailability = Pick<FlexPortion, 'numPortions' | 'isAvailable'>

// The API omits `isAvailable` when it does not know the availability (for example without a
// delivery date). If any portion has no flag (missing or null) we cannot say which options are
// available, so there is nothing accurate to report.
const splitPortionOptions = (portions: PortionAvailability[]) => {
  if (portions.some(({ isAvailable }) => typeof isAvailable !== 'boolean')) {
    return null
  }

  const available = portions.filter(({ isAvailable }) => isAvailable)
  const unavailable = portions.filter(({ isAvailable }) => !isAvailable)
  const toOptions = (options: PortionAvailability[]) =>
    options.map(({ numPortions }) => String(numPortions))

  return { available: toOptions(available), unavailable: toOptions(unavailable) }
}

export const trackEditRecipeQuantityLoaded = (
  context: Pick<FlexPortionTrackingContext, 'menuUuid' | 'recipeUuid'>,
  portions: PortionAvailability[],
) => {
  const options = splitPortionOptions(portions)

  if (!options) {
    return
  }

  queueTrackingEvent<EditRecipeQuantityLoadedEvent>({
    action: 'edit_recipe_quantity_loaded',
    properties: {
      available_portion_options: options.available,
      menu_uuid: context.menuUuid,
      recipe_uuid: context.recipeUuid,
      unavailable_portion_options: options.unavailable,
    },
  })
}

export const trackSelectPortionCount = (
  context: FlexPortionTrackingContext,
  recipePortionCount: number,
) => {
  queueTrackingEvent<SelectPortionCountEvent>({
    action: 'select_portion_count',
    properties: getCommonProperties(context, recipePortionCount),
  })
}

export const trackUpdatePortion = (
  context: FlexPortionTrackingContext,
  recipePortionCount: number,
) => {
  datadogRum.addAction('update_recipe_portions_flex', {
    numPortions: recipePortionCount,
    recipeId: context.recipeUuid,
  })

  queueTrackingEvent<UpdatePortionEvent>({
    action: 'update_portion',
    properties: getCommonProperties(context, recipePortionCount),
  })
}
